import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { StatTileProps, StatTileTone } from '../types/common.types';

const TONE_COLORS: Record<StatTileTone, { accent: string; iconBg: string; iconColor: string }> = {
  open: { accent: '#d6336c', iconBg: '#f8eef1', iconColor: '#a60f36' },
  closed: { accent: '#1f7a40', iconBg: '#ebf8ef', iconColor: '#1f7a40' },
  pending: { accent: '#b7791f', iconBg: '#fff7e8', iconColor: '#9a5a00' },
  'in-progress': { accent: '#1f56a5', iconBg: '#edf3ff', iconColor: '#1f56a5' },
  rejected: { accent: '#b42318', iconBg: '#fdeced', iconColor: '#b42318' },
  neutral: { accent: '#6b7c82', iconBg: '#f4f4f5', iconColor: '#4a4a4a' },
  brand: { accent: '#2F7E8E', iconBg: '#EAF3F5', iconColor: '#2F7E8E' },
};

// A single display/filter card — "Users: 5" style summary lines replaced with
// these across Departments Module and Approvals Flow, per the reference theme.
// Purely presentational; pass `active`/`onClick` to use one as a filter toggle.
const StatTile: React.FC<StatTileProps> = ({ label, value, caption, tone = 'neutral', icon, active, onClick }) => {
  const colors = TONE_COLORS[tone];
  const clickable = Boolean(onClick);

  return (
    <Box
      onClick={onClick}
      sx={{
        flex: '1 1 160px',
        minWidth: 150,
        backgroundColor: '#ffffff',
        border: active ? `1.5px solid ${colors.accent}` : '1px solid #e2eef1',
        borderRadius: 3,
        p: 1.5,
        cursor: clickable ? 'pointer' : 'default',
        transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
        boxShadow: active ? `0 1px 2px ${colors.accent}22` : 'none',
        ...(clickable
          ? {
              '&:hover': {
                borderColor: colors.accent,
              },
            }
          : {}),
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        {icon ? (
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: colors.iconBg,
              color: colors.iconColor,
              flexShrink: 0,
              '& svg': { fontSize: 18 },
            }}
          >
            {icon}
          </Box>
        ) : (
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: colors.accent, flexShrink: 0 }} />
        )}
        <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase', color: '#5b6b72' }}>
          {label}
        </Typography>
      </Stack>
      <Typography sx={{ fontSize: 24, fontWeight: 700, color: '#1E2A30', mt: 0.5, lineHeight: 1.15 }}>{value}</Typography>
      {caption ? (
        <Typography sx={{ fontSize: 11.5, color: '#8a979c', mt: 0.25 }}>{caption}</Typography>
      ) : null}
    </Box>
  );
};

export default StatTile;
